// SPDX-License-Identifier: AGPL-3.0-or-later
// Copyright (C) 2026 Ben Richardson <hi@ben.gy>
// Additional terms apply under AGPL-3.0 section 7(b) — see ADDITIONAL-TERMS.md

/*
 * A 16-bit greyscale PNG writer.
 *
 * This exists because Canvas 2D is 8 bits per channel and there is no way to
 * ask it for more. An 8-bit depth map bands visibly — 256 distinct distances
 * across a whole scene — and the banding is exactly what a downstream depth
 * consumer turns into terracing. So the depth map is written here, by hand,
 * from the raw float field.
 *
 * Everything below was checked against the W3C PNG 3rd edition rather than
 * recalled:
 *
 *  - Multi-byte integers are network byte order, most significant byte first
 *    (§7.1). That includes the SAMPLES, which is the part that goes wrong
 *    silently: viewing a Uint16Array as bytes inherits the platform's
 *    little-endian order and every pixel comes out transposed into noise.
 *    They are written MSB-then-LSB explicitly here.
 *  - Colour type 0 with bit depth 16 is legal (§11.2.1, table 12).
 *  - IDAT is zlib (§10.1, method code 8), which is exactly what
 *    `CompressionStream('deflate')` produces — the WHATWG spec defines
 *    "deflate" as RFC1950 zlib, and "raw deflate" is the other value,
 *    `deflate-raw`. Getting these two the wrong way round produces a file
 *    every decoder rejects.
 *  - CRC-32 is the standard reflected polynomial seeded all-ones and finally
 *    complemented (§5.5), stored MSB first like every other integer.
 *
 * FILTERS. The obvious implementation writes filter type 0 (None) on every row
 * and is four to ten times larger: measured at 1920x1080, filter 0 produced
 * 3,448,600 bytes against 698,919 for filter 2 (Up). libpng's per-row
 * minimum-sum-of-absolute-differences heuristic is also wrong here — on 16-bit
 * data the low byte of each sample is nearly noise and swamps the score, so it
 * picked Paeth for every row and came out 2.1x worse than uniform Up. So this
 * encoder compresses the whole image once per candidate filter and keeps the
 * smallest result, which is both simple and optimal over the candidates.
 */

const SIGNATURE = new Uint8Array([0x89, 0x50, 0x4e, 0x47, 0x0d, 0x0a, 0x1a, 0x0a]);

/** Bytes per sample pair — colour type 0 at bit depth 16 is 2 bytes/pixel. */
const BPP = 2;

const CRC_TABLE = (() => {
  const table = new Uint32Array(256);
  for (let n = 0; n < 256; n += 1) {
    let c = n;
    for (let k = 0; k < 8; k += 1) c = c & 1 ? 0xedb88320 ^ (c >>> 1) : c >>> 1;
    table[n] = c >>> 0;
  }
  return table;
})();

export function crc32(bytes: Uint8Array): number {
  let c = 0xffffffff;
  for (let i = 0; i < bytes.length; i += 1) c = CRC_TABLE[(c ^ bytes[i]!) & 0xff]! ^ (c >>> 8);
  return (c ^ 0xffffffff) >>> 0;
}

function chunk(type: string, body: Uint8Array): Uint8Array {
  const out = new Uint8Array(12 + body.length);
  const view = new DataView(out.buffer);
  view.setUint32(0, body.length); // big-endian is setUint32's default
  for (let i = 0; i < 4; i += 1) out[4 + i] = type.charCodeAt(i);
  out.set(body, 8);
  view.setUint32(8 + body.length, crc32(out.subarray(4, 8 + body.length)));
  return out;
}

/** PNG filter types this encoder will try. See the header note on why not 0. */
export const CANDIDATE_FILTERS = [1, 2, 4] as const;
export type FilterType = (typeof CANDIDATE_FILTERS)[number];

function paeth(a: number, b: number, c: number): number {
  const p = a + b - c;
  const pa = Math.abs(p - a);
  const pb = Math.abs(p - b);
  const pc = Math.abs(p - c);
  if (pa <= pb && pa <= pc) return a;
  return pb <= pc ? b : c;
}

/**
 * Filter every scanline with a single filter type and return the raw stream
 * that goes into IDAT: one filter-type byte, then the filtered bytes, per row.
 */
export function filterScanlines(
  samples: Uint16Array,
  width: number,
  height: number,
  filter: FilterType,
): Uint8Array {
  const rowBytes = width * BPP;
  const out = new Uint8Array((rowBytes + 1) * height);
  const raw = new Uint8Array(rowBytes);
  const prior = new Uint8Array(rowBytes);

  for (let y = 0; y < height; y += 1) {
    const base = y * width;
    for (let x = 0; x < width; x += 1) {
      const v = samples[base + x]! & 0xffff;
      raw[x * 2] = v >>> 8; // MSB first — PNG is network byte order
      raw[x * 2 + 1] = v & 0xff;
    }
    const at = y * (rowBytes + 1);
    out[at] = filter;
    for (let i = 0; i < rowBytes; i += 1) {
      const a = i >= BPP ? raw[i - BPP]! : 0;
      const b = prior[i]!;
      const c = i >= BPP ? prior[i - BPP]! : 0;
      const x = raw[i]!;
      let v: number;
      if (filter === 1) v = x - a;
      else if (filter === 2) v = x - b;
      else v = x - paeth(a, b, c);
      out[at + 1 + i] = v & 0xff;
    }
    prior.set(raw);
  }
  return out;
}

async function deflate(bytes: Uint8Array): Promise<Uint8Array> {
  // "deflate" is RFC1950 zlib, which is what PNG's IDAT requires.
  // "deflate-raw" would be RFC1951 and every decoder would reject the file.
  const stream = new Blob([bytes as BlobPart]).stream().pipeThrough(new CompressionStream('deflate'));
  return new Uint8Array(await new Response(stream).arrayBuffer());
}

export interface Png16Result {
  readonly bytes: Uint8Array;
  /** Which candidate filter won, so the build can assert the choice is sane. */
  readonly filter: FilterType;
}

/**
 * Encode a 16-bit greyscale PNG.
 *
 * `samples` is row-major, `width * height` values in [0, 65535].
 */
export async function encodeGrey16(
  samples: Uint16Array,
  width: number,
  height: number,
  filters: readonly FilterType[] = CANDIDATE_FILTERS,
): Promise<Png16Result> {
  if (!Number.isInteger(width) || !Number.isInteger(height) || width <= 0 || height <= 0) {
    throw new Error(`invalid PNG dimensions ${width}x${height}`);
  }
  if (samples.length !== width * height) {
    throw new Error(`expected ${width * height} samples, got ${samples.length}`);
  }

  const ihdr = new Uint8Array(13);
  const view = new DataView(ihdr.buffer);
  view.setUint32(0, width);
  view.setUint32(4, height);
  ihdr[8] = 16; // bit depth
  ihdr[9] = 0; // colour type: greyscale
  ihdr[10] = 0; // compression method: deflate
  ihdr[11] = 0; // filter method: adaptive
  ihdr[12] = 0; // interlace: none

  let best: { data: Uint8Array; filter: FilterType } | null = null;
  for (const filter of filters) {
    const data = await deflate(filterScanlines(samples, width, height, filter));
    if (!best || data.length < best.data.length) best = { data, filter };
  }
  if (!best) throw new Error('no candidate filters were given');

  const parts = [SIGNATURE, chunk('IHDR', ihdr), chunk('IDAT', best.data), chunk('IEND', new Uint8Array(0))];
  const total = parts.reduce((n, p) => n + p.length, 0);
  const bytes = new Uint8Array(total);
  let at = 0;
  for (const p of parts) {
    bytes.set(p, at);
    at += p.length;
  }
  return { bytes, filter: best.filter };
}

/**
 * Map a normalised [0,1] depth field to 16-bit samples.
 *
 * White is near. Depth Anything emits inverse depth, so the largest values are
 * the closest surfaces, and this preserves that convention — which is the one
 * every depth-map consumer expects.
 */
export function depthToSamples(depth: Float32Array): Uint16Array {
  const out = new Uint16Array(depth.length);
  for (let i = 0; i < depth.length; i += 1) {
    const v = depth[i]!;
    const c = v <= 0 ? 0 : v >= 1 ? 65535 : Math.round(v * 65535);
    out[i] = c;
  }
  return out;
}

/**
 * Read a 16-bit greyscale PNG back. Used by the test suite to prove the writer
 * round-trips byte-exactly, and by nothing in the shipped path.
 */
export async function decodeGrey16(
  bytes: Uint8Array,
): Promise<{ width: number; height: number; samples: Uint16Array }> {
  for (let i = 0; i < SIGNATURE.length; i += 1) {
    if (bytes[i] !== SIGNATURE[i]) throw new Error('not a PNG');
  }
  let at = 8;
  let width = 0;
  let height = 0;
  let filterMethod = -1;
  const idat: Uint8Array[] = [];
  const view = new DataView(bytes.buffer, bytes.byteOffset, bytes.byteLength);
  while (at < bytes.length) {
    const len = view.getUint32(at);
    const type = String.fromCharCode(bytes[at + 4]!, bytes[at + 5]!, bytes[at + 6]!, bytes[at + 7]!);
    const body = bytes.subarray(at + 8, at + 8 + len);
    const stored = view.getUint32(at + 8 + len);
    if (crc32(bytes.subarray(at + 4, at + 8 + len)) !== stored) throw new Error(`bad CRC on ${type}`);
    if (type === 'IHDR') {
      width = view.getUint32(at + 8);
      height = view.getUint32(at + 12);
      if (body[8] !== 16) throw new Error(`bit depth ${body[8]}, expected 16`);
      if (body[9] !== 0) throw new Error(`colour type ${body[9]}, expected 0`);
      filterMethod = body[11]!;
    } else if (type === 'IDAT') {
      idat.push(body.slice());
    } else if (type === 'IEND') {
      break;
    }
    at += 12 + len;
  }
  if (filterMethod !== 0) throw new Error('unexpected filter method');

  const joined = new Uint8Array(idat.reduce((n, p) => n + p.length, 0));
  let off = 0;
  for (const p of idat) {
    joined.set(p, off);
    off += p.length;
  }
  const raw = new Uint8Array(
    await new Response(
      new Blob([joined as BlobPart]).stream().pipeThrough(new DecompressionStream('deflate')),
    ).arrayBuffer(),
  );

  const rowBytes = width * BPP;
  const samples = new Uint16Array(width * height);
  const prior = new Uint8Array(rowBytes);
  const line = new Uint8Array(rowBytes);
  for (let y = 0; y < height; y += 1) {
    const base = y * (rowBytes + 1);
    const filter = raw[base]!;
    for (let i = 0; i < rowBytes; i += 1) {
      const x = raw[base + 1 + i]!;
      const a = i >= BPP ? line[i - BPP]! : 0;
      const b = prior[i]!;
      const c = i >= BPP ? prior[i - BPP]! : 0;
      let v: number;
      if (filter === 0) v = x;
      else if (filter === 1) v = x + a;
      else if (filter === 2) v = x + b;
      else if (filter === 3) v = x + ((a + b) >> 1);
      else v = x + paeth(a, b, c);
      line[i] = v & 0xff;
    }
    for (let x = 0; x < width; x += 1) {
      samples[y * width + x] = (line[x * 2]! << 8) | line[x * 2 + 1]!;
    }
    prior.set(line);
  }
  return { width, height, samples };
}
